import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import {
  MultiCheckboxSelect,
  type MultiCheckboxSelectOption,
} from '@/components/forms/multi-checkbox-select'
import { SearchInput } from '@/components/forms/search-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { useAdminListControls } from '@/features/admin/shared/use-admin-list-controls'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { ApiError } from '@/lib/api/http-client'
import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import actionImIconUrl from '@/assets/action-menu/im.svg'
import actionMembersIconUrl from '@/assets/action-menu/members.svg'
import actionViewIconUrl from '@/assets/action-menu/view.svg'

import { AdminAppFormDrawer } from './form-drawer'
import { AdminAppChannelsDrawer } from './channels-drawer'
import { AdminAppImDrawer } from './im-drawer'
import { AdminAppMembersDrawer } from './members-drawer'
import { deleteAdminApp, listAdminApps, listAdminAppTypes, type AdminApp } from './api'
import { formatAppType, formatDateTime, translateStatus } from './format'
import { AdminAppNameCell, AdminAppStatusCell } from './shared'
import { adminAppStatusFilterOrder } from './status'

const pageSizeOptions = [10, 20, 50]
const defaultAdminAppColumnIds = [
  'name',
  'description',
  'type',
  'status',
  'version',
  'created-at',
  'updated-at',
  'actions',
]

type AdminAppSortField = 'created_at' | 'name' | 'updated_at'
type AdminAppSortOrder = 'asc' | 'desc'

interface DeleteAppDialogProps {
  app?: AdminApp
  isSubmitting: boolean
  onClose: () => void
  onConfirm: () => void
  open: boolean
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.7" />
    </svg>
  )
}

export function AdminAppsPage() {
  const { t } = useTranslation('adminApps')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const {
    page,
    pageSize,
    search,
    searchInput,
    setPage,
    setPageSize,
    setSearchInput,
    setVisibleColumnIds,
    visibleColumnIds,
    visibleColumnIdSet,
  } = useAdminListControls({ defaultColumnIds: defaultAdminAppColumnIds })
  const [statuses, setStatuses] = useState<string[]>([])
  const [appType, setAppType] = useState('')
  const [sortField, setSortField] = useState<AdminAppSortField>('name')
  const [sortOrder, setSortOrder] = useState<AdminAppSortOrder>('desc')
  const [drawerState, setDrawerState] = useState<{
    appId?: string
    mode: 'create' | 'edit'
    open: boolean
    session: number
  }>({ mode: 'create', open: false, session: 0 })
  const [imDrawerApp, setImDrawerApp] = useState<AdminApp | null>(null)
  const [membersDrawerApp, setMembersDrawerApp] = useState<AdminApp | null>(null)
  const [channelsDrawerApp, setChannelsDrawerApp] = useState<AdminApp | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminApp | null>(null)

  const appTypesQuery = useQuery({
    queryKey: ['admin-app-types'],
    queryFn: listAdminAppTypes,
  })

  const appsQuery = useQuery({
    placeholderData: (previous) => previous,
    queryFn: () =>
      listAdminApps({
        appType,
        order: sortOrder,
        orderBy: sortField,
        page,
        pageSize,
        search,
        status: statuses,
      }),
    queryKey: ['admin-apps', { appType, page, pageSize, search, sortField, sortOrder, statuses }],
  })

  const deleteMutation = useMutation({
    mutationFn: (appId: string) => deleteAdminApp(appId),
    onError: (error) => {
      showFeedback({
        detail: error instanceof ApiError ? error.message : undefined,
        message: t('deleteDialog.failed'),
        scope: 'global',
        tone: 'error',
      })
    },
    onSuccess: () => {
      showFeedback({
        message: t('deleteDialog.success'),
        scope: 'global',
        tone: 'success',
      })
      setDeleteTarget(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-apps'] })
    },
  })

  const appTypeLabels = useMemo(
    () => new Map((appTypesQuery.data ?? []).map((item) => [item.type, item.label])),
    [appTypesQuery.data],
  )

  const appTypeOptions: SelectOption[] = useMemo(
    () => [
      { label: t('filters.allTypes'), value: '' },
      ...(appTypesQuery.data ?? []).map((item) => ({
        label: item.label,
        value: item.type,
      })),
    ],
    [appTypesQuery.data, t],
  )

  const statusOptions: MultiCheckboxSelectOption[] = useMemo(
    () =>
      adminAppStatusFilterOrder.map((status) => ({
        label: t(`status.${status}`),
        value: status,
      })),
    [t],
  )

  const toggleSort = useCallback(
    (field: AdminAppSortField) => {
      const nextOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc'

      setSortField(field)
      setSortOrder(nextOrder)
      setPage(1)
    },
    [setPage, sortField, sortOrder],
  )
  const columns = useMemo<DataTableColumn<AdminApp>[]>(() => {
    const sortDirection = (field: AdminAppSortField) => (sortField === field ? sortOrder : null)
    const sortActionLabel = (field: AdminAppSortField, ascKey: string, descKey: string) =>
      sortField === field && sortOrder === 'desc' ? t(ascKey) : t(descKey)

    return [
      {
        header: t('table.name'),
        headerAlign: 'center',
        hidden: !visibleColumnIdSet.has('name'),
        id: 'name',
        pin: 'left',
        render: (app) => <AdminAppNameCell app={app} />,
        visibilityLabel: t('table.name'),
        width: 260,
      },
      {
        header: t('table.description'),
        hidden: !visibleColumnIdSet.has('description'),
        id: 'description',
        render: (app) => app.description || t('emptyDescription'),
        visibilityLabel: t('table.description'),
        width: 220,
      },
      {
        header: t('table.type'),
        hidden: !visibleColumnIdSet.has('type'),
        id: 'type',
        render: (app) => formatAppType(app.app_type, appTypeLabels),
        visibilityLabel: t('table.type'),
        width: 140,
      },
      {
        header: t('table.status'),
        hidden: !visibleColumnIdSet.has('status'),
        id: 'status',
        render: (app) => (
          <AdminAppStatusCell status={app.status} text={translateStatus(t, app.status)} />
        ),
        visibilityLabel: t('table.status'),
        width: 130,
      },
      {
        header: t('table.version'),
        hidden: !visibleColumnIdSet.has('version'),
        id: 'version',
        render: (app) => app.version ?? '-',
        visibilityLabel: t('table.version'),
        width: 130,
      },
      {
        header: t('table.createdAt'),
        hidden: !visibleColumnIdSet.has('created-at'),
        id: 'created-at',
        render: (app) => formatDateTime(app.created_at),
        sort: {
          direction: sortDirection('created_at'),
          label: sortActionLabel('created_at', 'sort.createdAtAsc', 'sort.createdAtDesc'),
          onToggle: () => toggleSort('created_at'),
        },
        visibilityLabel: t('table.createdAt'),
        width: 190,
      },
      {
        header: t('table.updatedAt'),
        hidden: !visibleColumnIdSet.has('updated-at'),
        id: 'updated-at',
        render: (app) => formatDateTime(app.updated_at),
        sort: {
          direction: sortDirection('updated_at'),
          label: sortActionLabel('updated_at', 'sort.updatedAtAsc', 'sort.updatedAtDesc'),
          onToggle: () => toggleSort('updated_at'),
        },
        visibilityLabel: t('table.updatedAt'),
        width: 190,
      },
      {
        align: 'center',
        header: t('table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (app) => (
          <ActionMenu
            ariaLabel={t('actions.open', { name: app.name })}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'edit',
                label: t('actions.edit'),
                onSelect: () => {
                  setDrawerState((current) => ({
                    appId: app.id,
                    mode: 'edit',
                    open: true,
                    session: current.session + 1,
                  }))
                },
              },
              {
                iconSrc: actionImIconUrl,
                key: 'im',
                label: t('actions.im'),
                onSelect: () => setImDrawerApp(app),
              },
              {
                iconSrc: actionMembersIconUrl,
                key: 'members',
                label: t('actions.members'),
                onSelect: () => setMembersDrawerApp(app),
              },
              {
                iconSrc: actionViewIconUrl,
                key: 'channels',
                label: t('actions.channels'),
                onSelect: () => setChannelsDrawerApp(app),
              },
              {
                iconSrc: actionDeleteIconUrl,
                key: 'delete',
                label: t('actions.delete'),
                onSelect: () => setDeleteTarget(app),
                variant: 'danger',
              },
            ]}
          />
        ),
        visibilityLabel: t('table.actions'),
        width: 90,
      },
    ]
  }, [appTypeLabels, sortField, sortOrder, t, toggleSort, visibleColumnIdSet])

  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  const listData = appsQuery.data
  const apps = listData?.items ?? []
  const total = listData?.total ?? 0
  const showError = appsQuery.isError && !appsQuery.data
  const errorDescription =
    appsQuery.error instanceof ApiError
      ? t('errorDescriptionWithDetail', { detail: appsQuery.error.message })
      : t('errorDescription')
  const closeDeleteDialog = () => {
    if (deleteMutation.isPending) {
      return
    }

    setDeleteTarget(null)
  }

  return (
    <AdminShellPage activePath="/admin/apps">
      <section
        aria-label={t('workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-apps-workspace"
      >
        <PageHeader
          actions={
            <>
              <DataTableColumnSelector
                aria-label={t('columnSelector.label')}
                onValueChange={setVisibleColumnIds}
                options={columnSelectorOptions}
                value={visibleColumnIds}
              >
                {t('columnSelector.trigger')}
              </DataTableColumnSelector>
              <Button
                onClick={() => {
                  setDrawerState((current) => ({
                    mode: 'create',
                    open: true,
                    session: current.session + 1,
                  }))
                }}
                size="sm"
              >
                <PlusIcon />
                {t('actions.add')}
              </Button>
            </>
          }
          description={t('description')}
          title={t('title')}
        />
        <FilterBar>
          <SearchInput
            aria-label={t('filters.searchLabel')}
            onChange={(event) => {
              setSearchInput(event.target.value)
            }}
            placeholder={t('filters.searchPlaceholder')}
            value={searchInput}
          />
          <Select
            aria-label={t('filters.typeLabel')}
            disabled={appTypesQuery.isLoading}
            onValueChange={(value) => {
              setAppType(value)
              setPage(1)
            }}
            options={appTypeOptions}
            value={appType}
          />
          <MultiCheckboxSelect
            aria-label={t('filters.statusLabel')}
            allLabel={t('filters.allStatuses')}
            onValueChange={(value) => {
              setStatuses(value)
              setPage(1)
            }}
            options={statusOptions}
            value={statuses}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 lg:px-[30px]">
          {showError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => void appsQuery.refetch()}
                  size="sm"
                  variant="secondary"
                >
                  {t('retry')}
                </Button>
              }
              description={errorDescription}
              title={t('errorTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('empty')}
                getRowKey={(app) => app.id}
                isLoading={appsQuery.isLoading}
                rows={apps}
              />
              <Pagination
                disabled={appsQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                page={page}
                pageSize={pageSize}
                pageSizeOptions={pageSizeOptions}
                total={total}
              />
            </>
          )}
        </div>
        <AdminAppFormDrawer
          appId={drawerState.appId}
          appTypes={appTypesQuery.data ?? []}
          formSession={drawerState.session}
          mode={drawerState.mode}
          onOpenChange={(open) => {
            setDrawerState((current) => ({
              ...current,
              open,
            }))
          }}
          open={drawerState.open}
        />
        {channelsDrawerApp ? (
          <AdminAppChannelsDrawer
            app={channelsDrawerApp}
            key={channelsDrawerApp.id}
            onOpenChange={(open) => {
              if (!open) setChannelsDrawerApp(null)
            }}
          />
        ) : null}
        <AdminAppMembersDrawer
          app={membersDrawerApp}
          onOpenChange={(open) => {
            if (!open) {
              setMembersDrawerApp(null)
            }
          }}
          open={Boolean(membersDrawerApp)}
        />
        <AdminAppImDrawer
          app={imDrawerApp}
          onOpenChange={(open) => {
            if (!open) {
              setImDrawerApp(null)
            }
          }}
          open={Boolean(imDrawerApp)}
        />
        <DeleteAppDialog
          app={deleteTarget ?? undefined}
          isSubmitting={deleteMutation.isPending}
          onClose={closeDeleteDialog}
          onConfirm={() => {
            if (deleteTarget) {
              deleteMutation.mutate(deleteTarget.id)
            }
          }}
          open={Boolean(deleteTarget)}
        />
      </section>
    </AdminShellPage>
  )
}

function DeleteAppDialog({ app, isSubmitting, onClose, onConfirm, open }: DeleteAppDialogProps) {
  const { t } = useTranslation('adminApps')

  return (
    <Modal
      closeLabel={t('deleteDialog.close')}
      footer={
        <>
          <Button disabled={isSubmitting} onClick={onClose} size="sm" variant="secondary">
            {t('deleteDialog.cancel')}
          </Button>
          <Button isLoading={isSubmitting} onClick={onConfirm} size="sm" variant="danger">
            {t('deleteDialog.confirm')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      subtitle={t('deleteDialog.subtitle')}
      title={t('deleteDialog.title')}
    >
      <p className="text-sm leading-6 text-[var(--color-text)]">
        {t('deleteDialog.message', { name: app?.name ?? '' })}
      </p>
    </Modal>
  )
}
